import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  CalendarDays,
  Clock3,
  MapPin,
  Receipt,
  LoaderCircle,
  AlertCircle,
} from "lucide-react";
import { useApi } from "../utils/Api";
import studioImage from "../assets/studio-success.jpg";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const formatDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return `${d}/${m}/${y}`;
};

const formatTime = (value) => {
  if (!value) return "—";
  const [h, m] = value.split(":");
  const hour = Number(h);
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${suffix}`;
};

const ensureCoverageText = (value) => {
  if (!value) return "";
  const original = String(value).trim();
  return original.toLowerCase().includes("coverage")
    ? original
    : `${original} coverage`;
};

export default function EventPaymentSuccess() {
  const { apiFetch } = useApi();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState(null);

  const sessionId = searchParams.get("session_id");
  const type = searchParams.get("type");

  useEffect(() => {
    if (!sessionId) {
      setError("Missing payment session.");
      setLoading(false);
      return;
    }

    let interval;

    const fetchBooking = async () => {
      try {
        const endpoint = "verify_event_payment";
        const res = await apiFetch(endpoint, {
          session_id: sessionId,
        });

        if (!res?.success) {
          throw new Error(res?.message || "Unable to load booking.");
        }

        setBooking(res.data);

        if (res.data.payment_status === "paid" && interval) {
          clearInterval(interval);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
    interval = setInterval(fetchBooking, 3000);

    return () => clearInterval(interval);
  }, [sessionId, type, apiFetch]);

  const total = useMemo(() => money(booking?.package_price), [booking]);

  if (loading) {
    return (
      <main className="min-h-screen bg-black pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white p-10 text-center">
          <LoaderCircle className="mx-auto h-10 w-10 animate-spin text-black" />
          <p className="mt-4 text-gray-600">Loading booking...</p>
        </div>
      </main>
    );
  }

  if (error || !booking) {
    return (
      <main className="min-h-screen bg-black pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white p-10 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-red-500" />

          <h1 className="mt-4 text-xl font-semibold text-black">
            Unable to load booking
          </h1>

          <p className="mt-2 text-gray-600">{error}</p>

          <Link
            to="/packages"
            className="mt-6 inline-block rounded-lg bg-black px-5 py-3 text-white"
          >
            Back
          </Link>
        </div>
      </main>
    );
  }

  if (booking.payment_status !== "paid") {
    return (
      <main className="min-h-screen bg-black pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white p-10 text-center">
          <LoaderCircle className="mx-auto h-10 w-10 animate-spin text-black" />

          <h1 className="mt-6 text-2xl font-semibold text-black">
            Processing your payment...
          </h1>

          <p className="mt-2 text-gray-600">
            Please wait while we confirm your booking.
          </p>

          {booking.booking_ref && (
            <p className="mt-4 text-sm text-gray-600">
              Reference:{" "}
              <span className="font-medium text-black">
                {booking.booking_ref}
              </span>
            </p>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black pt-28 px-4 py-8">
      <div className="mx-auto max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="px-6 pb-8 pt-10 text-center">
          <CheckCircle2 size={62} className="mx-auto text-black" />

          <h1 className="mt-5 text-3xl font-semibold text-black">
            Booking Confirmed!
          </h1>

          <p className="mt-2 text-gray-600">Your event booking is confirmed.</p>

          {booking.booking_ref && (
            <p className="mt-3 text-sm text-gray-600">
              Reference:{" "}
              <span className="font-medium text-black">
                {booking.booking_ref}
              </span>
            </p>
          )}
        </div>

        <div className="h-[150px] w-full sm:h-[170px]">
          <img
            src={studioImage}
            alt="Event booking"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="px-6 py-8 text-black">
          <h2 className="text-[21px] font-medium">Event Details</h2>

          <div className="mt-7 space-y-6">
            <div>
              <p className="text-[17px] font-bold">{booking.package_name}</p>

              {booking.package_coverage && (
                <p className="mt-2 text-[17px] text-[#222]">
                  {ensureCoverageText(booking.package_coverage)}
                </p>
              )}

              {Array.isArray(booking.package_features) &&
                booking.package_features.length > 0 && (
                  <div className="mt-4">
                    

                    <ul className="mt-3 space-y-2">
                      {booking.package_features.map((feature, index) => (
                        <li
                          key={`feature-${index}`}
                          className="flex items-start gap-2 text-[16px] text-[#222]"
                        >
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-black shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>

            <div className="flex items-center gap-4">
              <CalendarDays size={24} />
              <p className="text-[17px]">{formatDate(booking.event_date)}</p>
            </div>

            <div className="flex items-center gap-4">
              <Clock3 size={24} />
              <p className="text-[17px]">{formatTime(booking.start_time)}</p>
            </div>

            {booking.location && (
              <div className="flex items-center gap-4">
                <MapPin size={24} />
                <p className="text-[17px]">{booking.location}</p>
              </div>
            )}
          </div>

          <div className="mt-12">
            <h2 className="text-[21px] font-medium">Payment</h2>

            <div className="mt-5 flex items-start gap-4">
              <Receipt size={24} />
              <p className="text-[17px]">
                <span className="font-medium">{total}</span> was successfully
                paid.
              </p>
            </div>
          </div>

          <div className="mt-10">
            <Link
              to="/packages"
              className="inline-flex rounded-xl bg-black px-5 py-3 text-sm font-semibold text-white"
            >
              Book another event
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
